import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalDialog,
  Option,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ArrowsClockwise as SyncIcon,
  ArrowCounterClockwise as RetryIcon,
  CheckCircle as ConnectedIcon,
  LinkBreak as DisconnectIcon,
  Plugs as ConnectIcon,
  WarningCircle as WarningIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import { ResponsiveTable, type ResponsiveColumn } from '../../components/common/ResponsiveTable';
import type {
  BackfillMode,
  InvoicePushMode,
  QuickBooksSyncLog,
  QuickBooksSyncStatus,
} from '../../types/quickbooks';

/**
 * Plain-English copy for the error codes the OAuth callback can redirect with.
 * Intuit's own wording ("invalid_grant") means nothing to an office admin, and
 * the callback is a redirect — there is no response body to carry a message.
 */
const CONNECT_ERROR_COPY: Record<string, string> = {
  // Intuit's own code when the admin backs out of the consent screen.
  access_denied: 'The QuickBooks sign-in was cancelled, so nothing was connected.',
  invalid_state:
    'That connection link had already been used or did not come from this page. Start again from here.',
  link_expired:
    'The connection link expired before the sign-in finished. Click Connect again — it only stays valid for a few minutes.',
  missing_code:
    'QuickBooks did not send back a sign-in key. Please try connecting again.',
  missing_realm:
    'QuickBooks did not say which company you picked. Try again and choose a company on the Intuit screen.',
  connection_failed:
    'QuickBooks accepted the sign-in but the connection could not be finished. Please try again, or contact support if it keeps happening.',
};

const SYNC_STATUS_COLOR: Record<QuickBooksSyncStatus, 'success' | 'danger' | 'warning' | 'neutral' | 'primary'> = {
  success: 'success',
  failed: 'danger',
  pending: 'warning',
  held: 'primary',
  skipped: 'neutral',
};

const SYNC_STATUS_LABEL: Record<QuickBooksSyncStatus, string> = {
  success: 'Sent',
  failed: 'Failed',
  pending: 'Waiting',
  held: 'Needs your OK',
  skipped: 'Skipped',
};

/** The editable half of QuickBooksSyncSettings — refs only; names ride along on save. */
interface SettingsDraft {
  income_account_ref: string;
  deposit_account_ref: string;
  default_item_ref: string;
  invoice_push_mode: InvoicePushMode;
}

const formatDateTime = (value: string | null): string =>
  value ? format(new Date(value), 'MMM d, yyyy h:mm a') : '—';

export const QuickBooksPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = Boolean(user?.is_organization_admin);

  const [banner, setBanner] = React.useState<{ color: 'success' | 'danger'; text: string } | null>(null);
  const [backfillMode, setBackfillMode] = React.useState<BackfillMode>('new_only');
  const [disconnectOpen, setDisconnectOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<SettingsDraft | null>(null);
  const [retryingId, setRetryingId] = React.useState<number | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const {
    data: status,
    isLoading: loadingStatus,
    isError: statusFailed,
  } = useQuery({
    queryKey: ['quickbooks-status'],
    queryFn: () => api.quickbooks.getStatus().then((res) => res.data),
  });

  const connection = status?.connection ?? null;
  // Layout follows whether a connection EXISTS, not whether it is currently
  // usable. `status.connected` is connection.is_usable, which goes false the
  // moment a token expires — driving the layout off it swapped a connected org
  // back to the first-run marketing card, hiding the reconnect button, the
  // disconnect button and the sync log exactly when they were needed most.
  const isConnected = Boolean(connection && connection.status !== 'disconnected');
  const isHealthy = Boolean(status?.connected);
  const needsReconnect = Boolean(connection?.needs_reconnect);

  // Intuit sends the admin back here after the consent screen. Read the
  // outcome, then strip the query string so a refresh (or a bookmark) doesn't
  // replay a stale "Connected!" banner.
  React.useEffect(() => {
    const connected = searchParams.get('connected');
    const errorCode = searchParams.get('error');
    if (!connected && !errorCode) return;

    if (connected === '1') {
      setBanner({ color: 'success', text: 'QuickBooks is connected. New invoices will start syncing.' });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-status'] });
    } else if (errorCode) {
      setBanner({
        color: 'danger',
        text:
          CONNECT_ERROR_COPY[errorCode] ??
          `QuickBooks could not finish connecting (${errorCode}). Try again, or contact support if it keeps happening.`,
      });
    }
    navigate('/settings/quickbooks', { replace: true });
  }, [searchParams, navigate, queryClient]);

  // The mapping lives on its own endpoint — the status payload only reports
  // whether it has been resolved yet, not what it is.
  const { data: settings = null } = useQuery({
    queryKey: ['quickbooks-settings'],
    queryFn: () => api.quickbooks.getSettings().then((res) => res.data),
    enabled: isConnected,
  });

  // 'all' rather than the default: the card shows recent activity, and the
  // default filter narrows to unresolved rows only, which made a healthy
  // connection look like it had never sent anything.
  const { data: logs = [], isLoading: loadingLogs } = useQuery({
    queryKey: ['quickbooks-logs'],
    queryFn: () => api.quickbooks.getLogs({ status: 'all', limit: 50 }).then((res) => res.data),
    enabled: isConnected,
  });

  // The pickers read live from the connected company, so they are only worth
  // fetching for someone who can actually change them on a usable connection.
  const { data: referenceData, isLoading: loadingReference } = useQuery({
    queryKey: ['quickbooks-reference-data'],
    queryFn: () => api.quickbooks.getReferenceData().then((res) => res.data),
    enabled: isAdmin && isHealthy && !needsReconnect,
    retry: false,
  });

  // Seed the form once. Re-seeding on every status refetch would throw away
  // edits in progress whenever a background poll lands.
  React.useEffect(() => {
    if (!settings) return;
    setDraft((current) =>
      current ?? {
        income_account_ref: settings.income_account_ref,
        deposit_account_ref: settings.deposit_account_ref,
        default_item_ref: settings.default_item_ref,
        invoice_push_mode: settings.invoice_push_mode,
      }
    );
  }, [settings]);

  const { mutate: connect, isPending: connecting } = useMutation({
    mutationFn: () => api.quickbooks.connect(settings?.backfill_mode ?? backfillMode),
    onSuccess: (res) => {
      // Leaves ApexOS for Intuit's consent screen; the callback brings us back.
      window.location.href = res.data.authorization_url;
    },
    onError: (error: Error) => setActionError(error.message || 'Could not start the QuickBooks connection.'),
  });

  const { mutate: disconnect, isPending: disconnecting } = useMutation({
    mutationFn: () => api.quickbooks.disconnect(),
    onSuccess: (res) => {
      setDisconnectOpen(false);
      setDraft(null);
      setBanner({
        color: 'success',
        text:
          res.data.message ||
          'QuickBooks has been disconnected. Anything already sent stays in QuickBooks.',
      });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-status'] });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-logs'] });
    },
    onError: (error: Error) => setActionError(error.message || 'Could not disconnect QuickBooks.'),
  });

  const { mutate: syncNow, isPending: syncing } = useMutation({
    mutationFn: () => api.quickbooks.syncNow(),
    onSuccess: (res) => {
      setBanner({
        color: 'success',
        text: res.data.message || 'Sync started. Anything waiting will be sent in the next few minutes.',
      });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-status'] });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-logs'] });
    },
    onError: (error: Error) => setActionError(error.message || 'Could not start a sync.'),
  });

  const { mutate: saveSettings, isPending: saving } = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to save.');
      const income = referenceData?.income_accounts.find((o) => o.id === draft.income_account_ref);
      const deposit = referenceData?.deposit_accounts.find((o) => o.id === draft.deposit_account_ref);
      const item = referenceData?.items.find((o) => o.id === draft.default_item_ref);
      // The *_name copies are sent alongside the refs so the page can show the
      // chosen account without asking Intuit again on every load.
      return api.quickbooks.updateSettings({
        income_account_ref: draft.income_account_ref,
        income_account_name: income?.name ?? settings?.income_account_name,
        deposit_account_ref: draft.deposit_account_ref,
        deposit_account_name: deposit?.name ?? settings?.deposit_account_name,
        default_item_ref: draft.default_item_ref,
        default_item_name: item?.name ?? settings?.default_item_name,
        invoice_push_mode: draft.invoice_push_mode,
      });
    },
    onSuccess: () => {
      setBanner({ color: 'success', text: 'QuickBooks settings saved.' });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-status'] });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-settings'] });
    },
    onError: (error: Error) => setActionError(error.message || 'Could not save the QuickBooks settings.'),
  });

  const { mutate: retryLog } = useMutation({
    mutationFn: (id: number) => api.quickbooks.retryLog(id),
    onMutate: (id: number) => setRetryingId(id),
    onSettled: () => setRetryingId(null),
    onSuccess: (res) => {
      setBanner({
        color: res.data.success ? 'success' : 'danger',
        // `message` is the log row's own plain-English outcome; `detail` only
        // appears on the 502 body, where it carries the QuickBooks error.
        text:
          res.data.detail ||
          res.data.log?.message ||
          (res.data.success ? 'Sent to QuickBooks.' : 'That item failed again.'),
      });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-status'] });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-logs'] });
    },
    onError: (error: Error) => setActionError(error.message || 'Could not retry that item.'),
  });

  // Held rows are the one thing the background sync will not move on its own.
  const { mutate: approveLog } = useMutation({
    mutationFn: (id: number) => api.quickbooks.approveLog(id),
    onMutate: (id: number) => setRetryingId(id),
    onSettled: () => setRetryingId(null),
    onSuccess: (res) => {
      setBanner({
        color: res.data.success ? 'success' : 'danger',
        text:
          res.data.detail ||
          res.data.log?.message ||
          (res.data.success ? 'Sent to QuickBooks.' : 'That invoice could not be sent.'),
      });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-status'] });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-logs'] });
    },
    onError: (error: Error) => setActionError(error.message || 'Could not send that invoice.'),
  });

  const isDirty = Boolean(
    draft &&
      settings &&
      (draft.income_account_ref !== settings.income_account_ref ||
        draft.deposit_account_ref !== settings.deposit_account_ref ||
        draft.default_item_ref !== settings.default_item_ref ||
        draft.invoice_push_mode !== settings.invoice_push_mode)
  );

  const logColumns: ResponsiveColumn<QuickBooksSyncLog>[] = [
    {
      key: 'item',
      header: 'Item',
      mobileVariant: 'header',
      render: (row) => (
        <Typography level="title-sm">
          {row.label || `${row.entity_type_display} #${row.local_id}`}
        </Typography>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '12%',
      render: (row) => <Typography level="body-sm">{row.entity_type_display}</Typography>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '12%',
      render: (row) => (
        <Chip size="sm" variant="soft" color={SYNC_STATUS_COLOR[row.status] ?? 'neutral'}>
          {row.status_display || SYNC_STATUS_LABEL[row.status] || row.status}
        </Chip>
      ),
    },
    {
      key: 'message',
      header: 'What happened',
      mobileVariant: 'body',
      render: (row) => (
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          {row.message || '—'}
        </Typography>
      ),
    },
    {
      key: 'attempted',
      header: 'Last tried',
      width: '18%',
      render: (row) => (
        <Typography level="body-sm">{formatDateTime(row.last_attempt_at || row.created_at)}</Typography>
      ),
    },
  ];

  if (loadingStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>
          QuickBooks
        </Typography>
        <PageHelpButton />
      </Box>

      {banner && (
        <Alert color={banner.color} variant="soft" sx={{ mb: 2 }} endDecorator={
          <Button size="sm" variant="plain" color={banner.color} onClick={() => setBanner(null)}>
            Dismiss
          </Button>
        }>
          {banner.text}
        </Alert>
      )}

      {actionError && (
        <Alert color="danger" variant="soft" sx={{ mb: 2 }} endDecorator={
          <Button size="sm" variant="plain" color="danger" onClick={() => setActionError(null)}>
            Dismiss
          </Button>
        }>
          {actionError}
        </Alert>
      )}

      {statusFailed && (
        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
          We could not load your QuickBooks connection just now. Refresh the page to try again.
        </Alert>
      )}

      {/* ApexOS's own Intuit app credentials are missing on this environment.
          Offering a Connect button here would only bounce the admin off to an
          Intuit error page. */}
      {status && !status.credentials_configured && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="h3" sx={{ mb: 1 }}>
              QuickBooks is not available yet
            </Typography>
            <Alert color="neutral" variant="soft">
              The QuickBooks connection has not been switched on for this ApexOS environment.
              Contact support and they will turn it on for your account.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* ---- Not connected ---- */}
      {status?.credentials_configured && !isConnected && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="h3" sx={{ mb: 1 }}>
              Send your invoices straight to QuickBooks
            </Typography>
            <Typography level="body-md" sx={{ mb: 2, color: 'text.secondary' }}>
              Connect the QuickBooks Online company your accountant uses and ApexOS will do the
              double entry for you. When you send an invoice, the homeowner is added to QuickBooks
              as a customer and the invoice goes with them. When you take a payment — deposit,
              progress or final — it gets applied against that same invoice. Nothing is pulled back
              out of QuickBooks, so your bookkeeper stays in charge of the books.
            </Typography>

            {isAdmin ? (
              <>
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Where should we start?</FormLabel>
                  <RadioGroup
                    value={backfillMode}
                    onChange={(event) => setBackfillMode(event.target.value as BackfillMode)}
                  >
                    <Radio
                      value="new_only"
                      label="Only new invoices from now on"
                      sx={{ mb: 1 }}
                    />
                    <Radio
                      value="open_invoices"
                      label="Also send my current open (unpaid) invoices"
                    />
                  </RadioGroup>
                  <FormHelperText>
                    Invoices that are already paid and closed stay in ApexOS either way — your
                    accountant has usually entered those already.
                  </FormHelperText>
                </FormControl>

                <Button
                  startDecorator={<ConnectIcon />}
                  onClick={() => connect()}
                  loading={connecting}
                >
                  Connect QuickBooks
                </Button>
              </>
            ) : (
              <Alert color="neutral" variant="soft">
                QuickBooks is connected by one of your organization's admins. Ask them to set it up.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Connected (or connected but broken) ---- */}
      {isConnected && connection && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {needsReconnect ? (
                    <WarningIcon color="var(--joy-palette-warning-500)" weight="fill" />
                  ) : (
                    <ConnectedIcon color="var(--joy-palette-success-500)" weight="fill" />
                  )}
                  <Typography level="h3">
                    {needsReconnect ? 'Reconnect needed' : 'Connected'}
                    {connection.company_name ? ` to ${connection.company_name}` : ''}
                  </Typography>
                </Stack>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Last synced{' '}
                  {connection.last_sync_at
                    ? formatDistanceToNow(new Date(connection.last_sync_at), { addSuffix: true })
                    : 'never — nothing has been sent yet'}
                </Typography>
              </Box>

              {/* Connecting and disconnecting change where the books live, so
                  they are admin-only. Running a sync is day-to-day work the
                  bookkeeper does, and the API allows it for the same reason —
                  a bookkeeper watching this screen is often not an org admin. */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {needsReconnect ? (
                  isAdmin && (
                    <Button startDecorator={<ConnectIcon />} onClick={() => connect()} loading={connecting}>
                      Reconnect
                    </Button>
                  )
                ) : (
                  <Button
                    variant="outlined"
                    startDecorator={<SyncIcon />}
                    onClick={() => syncNow()}
                    loading={syncing}
                  >
                    Sync now
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="outlined"
                    color="danger"
                    startDecorator={<DisconnectIcon />}
                    onClick={() => setDisconnectOpen(true)}
                  >
                    Disconnect
                  </Button>
                )}
              </Stack>
            </Stack>

            {needsReconnect && (
              <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                QuickBooks has signed ApexOS out — this happens on its own every few months, and
                also if someone removed ApexOS from the QuickBooks app list. Nothing has been lost:
                anything waiting will go over as soon as you sign back in. Click Reconnect and pick
                the same company.
              </Alert>
            )}

            {!needsReconnect && connection.error_message && (
              <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                {connection.error_message}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }}>
              <Box>
                <Typography level="body-xs" textColor="text.tertiary">
                  Waiting to send
                </Typography>
                <Typography level="body-md">{status?.pending_count ?? 0}</Typography>
              </Box>
              <Box>
                <Typography level="body-xs" textColor="text.tertiary">
                  Needs attention
                </Typography>
                <Typography level="body-md">{status?.failed_count ?? 0}</Typography>
              </Box>
              <Box>
                <Typography level="body-xs" textColor="text.tertiary">
                  Connected by
                </Typography>
                <Typography level="body-md">{connection.connected_by_name || '—'}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ---- Settings (admins only) ---- */}
      {isConnected && isAdmin && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="h3" sx={{ mb: 0.5 }}>
              How things are recorded
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
              Your accountant may want these pointed at particular accounts. The defaults work for
              most roofing companies.
            </Typography>

            {needsReconnect ? (
              <Alert color="neutral" variant="soft">
                Reconnect to QuickBooks to change these — we need a live connection to read your
                account list.
              </Alert>
            ) : (
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Income account</FormLabel>
                  <Select
                    value={draft?.income_account_ref ?? ''}
                    placeholder={loadingReference ? 'Loading your accounts…' : 'Choose an account'}
                    disabled={loadingReference || !referenceData}
                    onChange={(_event, value) =>
                      value && setDraft((d) => (d ? { ...d, income_account_ref: value } : d))
                    }
                  >
                    {(referenceData?.income_accounts ?? []).map((option) => (
                      <Option key={option.id} value={option.id}>
                        {option.name}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>Where the money you invoice is recorded as sales.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Deposit account</FormLabel>
                  <Select
                    value={draft?.deposit_account_ref ?? ''}
                    placeholder={loadingReference ? 'Loading your accounts…' : 'Choose an account'}
                    disabled={loadingReference || !referenceData}
                    onChange={(_event, value) =>
                      value && setDraft((d) => (d ? { ...d, deposit_account_ref: value } : d))
                    }
                  >
                    {(referenceData?.deposit_accounts ?? []).map((option) => (
                      <Option key={option.id} value={option.id}>
                        {option.name}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>
                    Where payments land before your bank deposit is made. Most offices leave this on
                    Undeposited Funds.
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Invoice line item</FormLabel>
                  <Select
                    value={draft?.default_item_ref ?? ''}
                    placeholder={loadingReference ? 'Loading your items…' : 'Choose an item'}
                    disabled={loadingReference || !referenceData}
                    onChange={(_event, value) =>
                      value && setDraft((d) => (d ? { ...d, default_item_ref: value } : d))
                    }
                  >
                    {(referenceData?.items ?? []).map((option) => (
                      <Option key={option.id} value={option.id}>
                        {option.name}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>
                    Every line on an ApexOS invoice is billed against this one QuickBooks item, so
                    your item list stays short. The line descriptions and prices still carry over.
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>When invoices are sent to QuickBooks</FormLabel>
                  <Select
                    value={draft?.invoice_push_mode ?? 'automatic'}
                    onChange={(_event, value) =>
                      value &&
                      setDraft((d) => (d ? { ...d, invoice_push_mode: value as InvoicePushMode } : d))
                    }
                  >
                    <Option value="automatic">Automatically, as soon as I send them</Option>
                    <Option value="review">Hold them for me to review first</Option>
                  </Select>
                </FormControl>

                <Box>
                  <Button onClick={() => saveSettings()} loading={saving} disabled={!isDirty}>
                    Save changes
                  </Button>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Sync activity ---- */}
      {isConnected && (
        <Card>
          <CardContent>
            <Typography level="h3" sx={{ mb: 0.5 }}>
              Sync activity
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
              Everything ApexOS has tried to send. A payment recorded before its invoice reached
              QuickBooks waits here and goes over on its own once the invoice lands.
            </Typography>

            {loadingLogs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size="sm" />
              </Box>
            ) : (
              <ResponsiveTable<QuickBooksSyncLog>
                rows={logs}
                getRowKey={(row) => row.id}
                columns={logColumns}
                rowActions={(row) => {
                  if (row.status === 'held') {
                    return (
                      <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        loading={retryingId === row.id}
                        onClick={() => approveLog(row.id)}
                      >
                        Send to QuickBooks
                      </Button>
                    );
                  }
                  if (row.status === 'failed') {
                    return (
                      <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<RetryIcon />}
                        loading={retryingId === row.id}
                        onClick={() => retryLog(row.id)}
                      >
                        Retry
                      </Button>
                    );
                  }
                  return null;
                }}
                emptyState={
                  <Alert color="neutral" variant="soft">
                    Nothing has been sent to QuickBooks yet. Your next invoice will show up here.
                  </Alert>
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      <Modal open={disconnectOpen} onClose={() => setDisconnectOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480 }}>
          <Typography level="h4" sx={{ mb: 1 }}>
            Disconnect QuickBooks?
          </Typography>
          <Typography level="body-md" sx={{ mb: 2 }}>
            Everything ApexOS has already sent stays in QuickBooks — invoices, customers and
            payments are untouched. Disconnecting only stops new ones from going over. You can
            connect again at any time.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="plain" color="neutral" onClick={() => setDisconnectOpen(false)}>
              Keep connected
            </Button>
            <Button color="danger" onClick={() => disconnect()} loading={disconnecting}>
              Disconnect
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default QuickBooksPage;
